import {isSearchUrl} from '@github-ui/code-view-shared/components/files-search/FilesSearchBox'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import type {CollapseTreeFunction, ExpandTreeFunction} from '@github-ui/code-view-types'
import {getCookie, setCookie} from '@github-ui/cookies'
import {useCurrentUser} from '@github-ui/current-user'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize, useScreenSize} from '@github-ui/screen-size'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {Heading} from '@primer/react'
import {useCallback, useEffectEvent, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {ExpandFileTreeButton, type ExpandFileTreeButtonProps} from '../components/ExpandFileTreeButton'
import {TreeOverlayBreakpoint} from '../components/ReposFileTreePane'
import styles from './use-tree-pane.module.css'

export interface TreePane {
  isTreeExpanded: boolean
  treeToggleElement: React.ReactElement
  treeToggleRef: React.RefObject<HTMLButtonElement | null>
  searchBoxRef: React.RefObject<HTMLInputElement | null>
  expandTree: ExpandTreeFunction
  collapseTree: CollapseTreeFunction
}

export function useTreePane(
  reposFileTreeId: string,
  openPanelRef: React.RefObject<string | undefined>,
  treeExpanded: boolean,
  updateExpandPreferences?: (expanded: boolean) => void,
  cookieName = 'fileTreeExpanded',
  expandFileTreeButtonProps?: Partial<ExpandFileTreeButtonProps>,
  // An extra panel (e.g. the Copilot side panel) that occupies docked space next
  // to the content, like the symbol viewer does. When it is open, the file nav
  // collapses below TreeOverlayBreakpoint instead of the narrower xlarge width.
  additionalPanelOpenRef?: React.RefObject<boolean>,
): TreePane {
  const {sendRepoClickEvent} = useReposAnalytics()
  const currentUser = useCurrentUser()

  const fileTreeExpandedCookie = cookieName ? getCookie(cookieName) : undefined
  const isSSR = !!(typeof ssrSafeDocument === 'undefined')
  // Only use the cookie if the user isn't logged in
  let initiallyExpanded =
    (!currentUser && fileTreeExpandedCookie && fileTreeExpandedCookie.value !== 'false') ||
    (currentUser && treeExpanded)

  if (initiallyExpanded === undefined) {
    initiallyExpanded = false
  }

  const treeToggleRef = useRef<HTMLButtonElement>(null)
  const searchBoxRef = useRef<HTMLInputElement>(null)
  const {screenSize} = useScreenSize()

  const [isTreeExpanded, setIsTreeExpanded] = useState(initiallyExpanded)
  // Keep track of the last state that was specifically requested by the user
  const lastStateIsExpanded = useRef(initiallyExpanded)
  const expandTreeCookieExpiration = 30 * 24 * 60 * 60 * 1000 // 30 days
  const expandedAsOverlay = useRef(false)
  const hasManuallyCollapsed = useRef(false)

  const showAsOverlay = useCallback(() => {
    const hasPanel = !!openPanelRef.current || !!additionalPanelOpenRef?.current
    return !(
      (hasPanel && window.matchMedia(`(min-width: ${TreeOverlayBreakpoint}px)`).matches) ||
      (!hasPanel && window.matchMedia(`(min-width: ${ScreenSize.xlarge}px)`).matches)
    )
  }, [openPanelRef, additionalPanelOpenRef])

  /**
   * With SSR, we need to make sure we get the correct initial state for the tree
   */
  const updateTreeExpandedForOverlay = useEffectEvent(() => {
    const showingAsOverlay = showAsOverlay()
    if (!showingAsOverlay) {
      expandedAsOverlay.current = false
    }
    const shouldExpand =
      (isSearchUrl() && screenSize < ScreenSize.large && !hasManuallyCollapsed.current) ||
      ((!showingAsOverlay || expandedAsOverlay.current) &&
        ((currentUser && isTreeExpanded) || (!currentUser && fileTreeExpandedCookie?.value !== 'false')))
    setIsTreeExpanded(shouldExpand)
  })

  // Don't retrigger when the tree expanded state changes.
  useLayoutEffect(() => {
    updateTreeExpandedForOverlay()
  }, [expandedAsOverlay, fileTreeExpandedCookie?.value, screenSize, openPanelRef, showAsOverlay, currentUser])

  const isTreeExpandedRef = useRef(isTreeExpanded)
  useLayoutEffect(() => {
    isTreeExpandedRef.current = isTreeExpanded
  })

  /**
   * When the screen size shrinks below medium, collapse the tree if it is expanded.
   * When the screen size grows beyond medium, return the tree to its last state.
   *
   * When the screen size shrinks below large, collapse the tree if it is expanded.
   * When the screen size grows beyond large, return the tree to its last state.
   */
  useLayoutEffect(() => {
    const hasPanel = !!openPanelRef.current || !!additionalPanelOpenRef?.current
    const breakpoint = hasPanel ? TreeOverlayBreakpoint : ScreenSize.xlarge

    const shouldClose = screenSize < breakpoint
    const shouldOpen = screenSize >= breakpoint

    if (shouldClose && lastStateIsExpanded.current && !isSearchUrl() && isTreeExpandedRef.current) {
      setIsTreeExpanded(false)
    }
    if (shouldOpen && lastStateIsExpanded.current && !isTreeExpandedRef.current) {
      setIsTreeExpanded(true)
    }
  }, [openPanelRef, additionalPanelOpenRef, screenSize])

  const expandTree: ExpandTreeFunction = useCallback(
    options => {
      setIsTreeExpanded(true)
      if (showAsOverlay()) {
        expandedAsOverlay.current = true
      }

      if (options?.setPreference) {
        updateExpandPreferences?.(true)
        lastStateIsExpanded.current = true
        if (cookieName) {
          const expireTime = new Date(Date.now() + expandTreeCookieExpiration).toUTCString()
          setCookie(cookieName, 'true', expireTime)
        }
      }

      if (options?.focus === 'toggleButton') {
        requestAnimationFrame(() => treeToggleRef.current?.focus())
      } else if (options?.focus === 'search') {
        requestAnimationFrame(() => searchBoxRef.current?.focus())
      }
    },
    [cookieName, expandTreeCookieExpiration, showAsOverlay, updateExpandPreferences],
  )

  const collapseTree: CollapseTreeFunction = useCallback(
    options => {
      setIsTreeExpanded(false)
      expandedAsOverlay.current = false
      hasManuallyCollapsed.current = true
      if (options?.setPreference) {
        updateExpandPreferences?.(false)
        lastStateIsExpanded.current = false
        if (cookieName) {
          const expireTime = new Date(Date.now() + expandTreeCookieExpiration).toUTCString()
          setCookie(cookieName, 'false', expireTime)
        }
      }

      if (options?.focus === 'toggleButton') {
        requestAnimationFrame(() => treeToggleRef.current?.focus())
      }
    },
    [cookieName, expandTreeCookieExpiration, updateExpandPreferences],
  )

  // This is SSR safe since it won't be called during SSR
  const shouldSetPreference = useCallback(
    (openPanel: string | undefined) => {
      const hasPanel = !!openPanel || !!additionalPanelOpenRef?.current
      const hasPanelRef = !!openPanelRef.current || !!additionalPanelOpenRef?.current
      return (
        (hasPanel && window.matchMedia(`(min-width: ${TreeOverlayBreakpoint}px)`).matches) ||
        (!hasPanelRef && window.matchMedia(`(min-width: ${ScreenSize.xlarge}px)`).matches)
      )
    },
    [openPanelRef, additionalPanelOpenRef],
  )

  const onToggleExpanded = useCallback(() => {
    sendRepoClickEvent(isTreeExpandedRef.current ? 'FILES_TREE.HIDE' : 'FILES_TREE.SHOW')
    // On the overview page, the toggle button isn't sticky, we don't want the user to lose the place
    // on the page to focus it if they click
    if (isTreeExpandedRef.current) {
      collapseTree({
        focus: 'toggleButton',
        setPreference: shouldSetPreference(openPanelRef.current),
      })
    } else {
      expandTree({focus: 'toggleButton', setPreference: shouldSetPreference(openPanelRef.current)})
    }
  }, [sendRepoClickEvent, collapseTree, shouldSetPreference, openPanelRef, expandTree])

  const toggleButtonClassName =
    fileTreeExpandedCookie === undefined && !isTreeExpanded && !isSSR
      ? 'react-tree-toggle-button-with-indicator'
      : undefined

  const treeToggleElement = useMemo(
    () => (
      <Heading as="h2" className={styles.Heading}>
        <ExpandFileTreeButton
          expanded={isTreeExpanded}
          ariaControls={reposFileTreeId}
          onToggleExpanded={onToggleExpanded}
          className={toggleButtonClassName}
          ref={treeToggleRef}
          {...expandFileTreeButtonProps}
        />
      </Heading>
    ),
    [isTreeExpanded, reposFileTreeId, onToggleExpanded, toggleButtonClassName, expandFileTreeButtonProps],
  )

  return useMemo(
    () => ({
      isTreeExpanded,
      expandTree,
      collapseTree,
      treeToggleElement,
      treeToggleRef,
      searchBoxRef,
    }),
    [isTreeExpanded, expandTree, collapseTree, treeToggleElement, treeToggleRef, searchBoxRef],
  )
}
